import React, { useContext, useEffect, useRef, useState } from "react";
import { PaymentRequestButtonElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { AuthContext } from "../../../../../provider/authProvider";
import usePaymentCheckoutState from "../../../../../zustand/payment/checkout/paymentCheckoutState";
import useAuthState from "../../../../../zustand/authState";
import { getAPIAxios } from "../../../../../helper/constants/uri";
import { trackEvent } from "../../../../../helper/tracking/tracking";
import ShLoadingIndicatorModalLazy from "../../../../../components/loading-indicator/shLoadingIndicatorModalLazy";
import ShUserMessage from "../../../../../components/user-message/user-message";
import styles from "../../../checkout-generic/payment/shPaymentAction.module.scss";
import { checkStripeUpdate } from "../../helper/stripe/shStripeInfoChecker";

const ShCheckoutStripePaymentButtons = (
    {
        label, totalPrice,
        url = null,
        postData,
        paymentSucceeded = (params: { transactionId: string, transactionDate: string} ) => { }
    } ) => {

    const stripe = useStripe();
    const elements = useElements();

    const [paymentRequest, setPaymentRequest] = useState(null);

    const once = useRef(false);
    const authContext = useContext(AuthContext);

    const [ processing, setProcessing ] = useState(false);
    const [ errorMessage, setErrorMessage ]= useState(null);

    const setHasWalletPayment = usePaymentCheckoutState(s => s.setHasWalletPayment);

    const postDataRef = useRef();

    useEffect(
        () => {
            postDataRef.current = postData;
        },
        [ postData ]
    )

    useEffect(
        () => {

            if (!totalPrice || !stripe || !elements) {
                return;
            }

            const action = async () => {
                const currentUserOverview = useAuthState.getState().currentUserOverview;

                if (once.current) {
                    paymentRequest?.update({
                        total: { label, amount: totalPrice * 100 }
                    });

                    return;
                }

                // pending -> might change because of new discount code etc.
                const amount = Math.round(totalPrice * 100 * 100) / 100;

                const pr = stripe.paymentRequest({
                    country: currentUserOverview.country,
                    currency: 'eur',
                    displayItems: [
                        { label, amount, pending: true }
                    ],
                    total: { label: "7hauben", amount, pending: true }
                });

                once.current = true;

                pr.canMakePayment().then(result => {
                    if (!result) {
                        return;
                    }

                    setHasWalletPayment(true);
                    setPaymentRequest(pr);

                    pr.on(
                        'paymentmethod',
                        async (ev) => {
                            if (! (await authContext.prioAction())) return;

                            // already working on it
                            if (processing) {
                                return;
                            }

                            setProcessing(true);

                            const axios = getAPIAxios();
                            trackEvent("stripe payment payment intent", { postData: postDataRef.current } );

                            try {
                                // perform standard transaction
                                const res = await axios.post(url, postDataRef.current);

                                const clientSecret = res.data.clientSecret;
                                const setupIntentClientSecret = res.data.setupIntentClientSecret;

                                let payload;

                                if (clientSecret) {
                                    payload = await stripe.confirmCardPayment(clientSecret,
                                        {payment_method: ev.paymentMethod.id},
                                        {handleActions: false}
                                    );
                                }

                                if (setupIntentClientSecret) {
                                    payload = await stripe.confirmCardSetup(setupIntentClientSecret,
                                        {payment_method: ev.paymentMethod.id},
                                        {handleActions: false}
                                    );
                                }

                                if (payload.error) {
                                    setErrorMessage(`${payload.error.message}`);
                                    ev.complete('fail');

                                    trackEvent("stripe transaction payment buttons error purchase ", { error: payload.error } );

                                    await authContext.updateOwnOverview();
                                } else {
                                    ev.complete('success');
                                    const stripeInfo  = await checkStripeUpdate();

                                    if (!stripeInfo) {
                                        setErrorMessage("Eventuell dauert es noch einen Moment bis die Zahlung bei uns angekommen ist.");
                                        await paymentSucceeded(null);
                                    } else {
                                        await paymentSucceeded(stripeInfo);
                                    }

                                    setErrorMessage(null);
                                }

                            } catch(err) {
                                setErrorMessage("Unbekannter Fehler passiert");
                                ev.complete('fail');

                                trackEvent("stripe payment buttons error catch ", { error: err } );

                                await authContext.updateOwnOverview();
                            } finally {
                                setProcessing(false);
                            }

                        }
                    )
                });
            };

            action();
        },
        [ stripe, elements, totalPrice ]
    );

    if (paymentRequest) {

        return <>
            <ShLoadingIndicatorModalLazy isOpen={processing} />
            <PaymentRequestButtonElement className={"mt-4"} options={ { paymentRequest } } />
            <ShUserMessage error={true} message={errorMessage} className="mt-2 ml-0"/>

            <div className={"d-block " + styles.infoLine}>
                <p className={"px-4 py-2 text-center w-auto bg-solid-cream-01"}> oder</p>
                <div>
                    <hr className={"my-4 opacity-25"} />
                </div>
            </div>
        </>;

    }

    return null;

};

export default ShCheckoutStripePaymentButtons;
